import React, { useEffect, useRef, useState } from 'react';

import styled from 'styled-components';

import FetchModule from '../../Share/Network/FetchModule';
import { useLanguage } from '../../../Context/LanguageContext';
import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';

import TotalModalWrapper from '../../Share/Normal/TotalModalWrapper';
import UserProfile from '../../../Component/Normal/UserProfile';

import { ReactComponent as SearchIcon } from '../../../image/Searching.svg';
import { ReactComponent as CheckIcon } from '../../../image/Check.svg';
import { ReactComponent as ExitButtonSvg } from '../../../image/ExitButton.svg';
import DefaultLogoPath from '../../../image/DefaultLogo.png';
import PhoneImage from '../../../image/PhoneIcon.png';
import LocationImage from '../../../image/LocationIcon.png';
import PrevImage from '../../../image/WhiteLeftTriangle.png';
import NextImage from '../../../image/WhiteRightTriangle.png';
import NonePictureImage from '../../../image/NonePictureImage.png';

export default function CenterFollowModal(props) {
  const {
    isExercise,
    isFollow,
    follows,
    followers,
    initialClickData,
    onClickFollow,
    close
  } = props;

  const { t } = useLanguage();

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [searchValue, setSearchValue] = useState('');
  const [stores, setStores] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [clickData, setClickData] = useState(initialClickData);
  const [pictures, setPictures] = useState([]);
  const [nowPictureIndex, setNowPictureIndex] = useState(0);

  const CenterPictureRef = useRef(null);
  // 상세보기 다이렉트 진입 여부 — true면 x 클릭 시 리스트로 돌아가지 않고 즉시 모달 닫기 //
  const isDirectDetailEntryRef = useRef(initialClickData !== undefined);

  // 업체 리스트 불러오기 (팔로우 검색 모달에서만 필요) //
  useEffect(() => {
    if (!isFollow) return;

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.getDataVersion2('store/all_store', 'GET');

        if (responseData.status === 200) setStores(responseData.data);
      }
    )();
  }, [isFollow])

  // 한글/영문 검색 매칭 //
  useEffect(() => {
    const source = isFollow ? stores : (followers || []);
    const keyword = searchValue.toLowerCase().replaceAll(' ', '');

    if (keyword === '') {
      setFilteredData(source);
      return;
    }

    const nameMatches = [];
    const addressMatches = [];

    for (const item of source) {
      const name = (item.storeName || '').toLowerCase().replaceAll(' ', '');
      const address = (item.storeAddress || '').toLowerCase().replaceAll(' ', '');

      if (name.includes(keyword)) nameMatches.push(item);
      else if (address.includes(keyword)) addressMatches.push(item);
    }

    setFilteredData(nameMatches.concat(addressMatches));
  }, [stores, followers, searchValue, isFollow])

  // 선택된 센터의 사진 정렬 //
  useEffect(() => {
    if (clickData === undefined) {
      setPictures([]);
      setNowPictureIndex(0);
      return;
    }

    const pictureData = (clickData.profiles || '').split(',').filter(p => p !== '');

    setPictures(pictureData);
    setNowPictureIndex(0);
  }, [clickData])

  // 닫기 클릭시 — 다이렉트 진입은 즉시 종료, 리스트 경유 진입에서 드릴다운 상태면 리스트로 복귀 //
  function onClickExitButton() {
    if (clickData !== undefined && !isDirectDetailEntryRef.current) {
      setClickData(undefined);
      return;
    }

    close();
  }

  // 행 좌측(프로필+텍스트) 클릭시 — 드릴다운 진입 //
  function onClickRowDetail(data) {
    const decoratedData = { ...data };

    if (isFollow) {
      const idx = follows.findIndex(e => e.storeId === data.storeId);

      if (idx !== -1) {
        const matched = follows[idx];

        if (matched.shareId !== undefined) decoratedData.shareId = matched.shareId;
        if (matched.partnerId !== undefined) decoratedData.partnerId = matched.partnerId;
        decoratedData.status = matched.status;
        decoratedData.profiles = matched.profiles !== undefined ? matched.profiles : data.profiles;
        decoratedData.storeTel = matched.storeTel !== undefined ? matched.storeTel : data.storeTel;
      }
    }

    setClickData(decoratedData);
  }

  // 드릴다운에서 액션 버튼 클릭시 //
  function onClickFollowAction() {
    onClickFollow(clickData);
    setClickData(undefined);
  }

  // 액션 버튼 색상 (status / variant 분기) //
  function calcActionButtonColor() {
    if (clickData === undefined) return theme.subKey;

    if (isFollow) {
      if (clickData.status === undefined) return theme.subKey;
      if (!clickData.status) return theme.primary;

      return theme.pointRed;
    }

    if (!clickData.status) return theme.primary;

    return theme.pointRed;
  }

  // 액션 버튼 텍스트 (status / variant 분기) //
  function calcActionButtonText() {
    if (clickData === undefined) return '';

    if (isFollow) {
      if (clickData.status === undefined) return t('myStoreSetting.followModal.followApply');
      if (!clickData.status) return t('myStoreSetting.followModal.followCancel');

      return t('myStoreSetting.followModal.followDisconnect');
    }

    if (!clickData.status) return t('myStoreSetting.followModal.followApprove');

    return t('myStoreSetting.followModal.followDisconnect');
  }

  // 전화번호 정리 //
  function calcGymTel(gymTel) {
    if (!gymTel) return '';

    const calcTel = gymTel.replaceAll('-', '');

    if (calcTel.length === 12) return calcTel.slice(0, 4) + '-' + calcTel.slice(4, 8) + '-' + calcTel.slice(8, 12);
    if (calcTel.slice(0, 3) === '010') return calcTel.replace(/[^0-9]/, '').replace(/^(\d{2,3})(\d{3,4})(\d{4})$/, `$1-$2-$3`);

    return calcTel.slice(0, 3) + '-' + calcTel.slice(3, 6) + '-' + calcTel.slice(6, 10);
  }

  // 사진 이전 버튼 클릭시 //
  function onClickPrevPicture() {
    if (nowPictureIndex <= 0) return;

    if (CenterPictureRef.current) CenterPictureRef.current.scrollTo({ left: 342 * (nowPictureIndex - 1), behavior: 'smooth' });
    setNowPictureIndex(nowPictureIndex - 1);
  }

  // 사진 다음 버튼 클릭시 //
  function onClickNextPicture() {
    if (nowPictureIndex >= pictures.length - 1) return;

    if (CenterPictureRef.current) CenterPictureRef.current.scrollTo({ left: 342 * (nowPictureIndex + 1), behavior: 'smooth' });
    setNowPictureIndex(nowPictureIndex + 1);
  }

  // 행 상태 계산 — 팔로우 검색 variant //
  function getFollowRowState(data) {
    // 파트너 모드: follows + followers 모두 검사하여 양방향 상태 감지 //
    if (!isExercise) {
      const followMatch = follows.find(e => e.storeId === data.storeId);
      if (followMatch) {
        if (followMatch.status === true) return { label: t('myStoreSetting.followModal.partnerRowConnected'), variant: 'following' };
        return { label: t('myStoreSetting.followModal.partnerRowPending'), variant: 'pending' };
      }

      const followerMatch = (followers || []).find(e => e.storeId === data.storeId);
      if (followerMatch) {
        if (followerMatch.status === true) return { label: t('myStoreSetting.followModal.partnerRowConnected'), variant: 'following' };
        return { label: t('myStoreSetting.followModal.partnerRowAcceptPending'), variant: 'pending' };
      }

      return { label: '', variant: 'add' };
    }

    // 프로그램 모드: 기존 동작 유지 //
    const idx = follows.findIndex(e => e.storeId === data.storeId);

    if (idx === -1) return { label: t('myStoreSetting.followModal.rowFollowing'), variant: 'add' };

    const matched = follows[idx];

    if (matched.status === false) return { label: t('myStoreSetting.followModal.rowPending'), variant: 'pending' };

    return { label: t('myStoreSetting.followModal.rowFollower'), variant: 'following' };
  }

  // 행 상태 계산 — 팔로워 관리 variant //
  function getFollowerRowState(data) {
    const connectedKey = isExercise ? 'rowFollowing' : 'partnerRowConnected';

    if (data.status === false) return { label: t('myStoreSetting.followModal.rowPendingApprove'), variant: 'add' };

    return { label: t('myStoreSetting.followModal.' + connectedKey), variant: 'following' };
  }

  // 행 클릭시 — 상태/variant에 따라 데이터 보강 후 부모 핸들러 호출 //
  function onClickRow(data) {
    if (isFollow) {
      const idx = follows.findIndex(e => e.storeId === data.storeId);
      const decoratedData = { ...data };

      if (idx !== -1) {
        const matched = follows[idx];

        if (matched.shareId !== undefined) decoratedData.shareId = matched.shareId;
        if (matched.partnerId !== undefined) decoratedData.partnerId = matched.partnerId;
        decoratedData.status = matched.status;
      }

      onClickFollow(decoratedData);
    }
    else {
      onClickFollow(data);
    }
  }

  // 모달 타이틀 / 설명 분기 //
  function getTitle() {
    if (isExercise && isFollow) return t('myStoreSetting.followModal.programFollowTitle');
    if (isExercise && !isFollow) return t('myStoreSetting.followModal.programFollowerTitle');
    if (!isExercise && isFollow) return t('myStoreSetting.followModal.partnerFollowTitle');
    return t('myStoreSetting.followModal.partnerFollowerTitle');
  }

  // 모달 설명 분기 //
  function getDescription() {
    if (isExercise && isFollow) return t('myStoreSetting.followModal.programFollowDescription');
    if (isExercise && !isFollow) return t('myStoreSetting.followModal.programFollowerDescription');
    if (!isExercise && isFollow) return t('myStoreSetting.followModal.partnerFollowDescription');
    return t('myStoreSetting.followModal.partnerFollowerDescription');
  }

  // 리스트 비어있을 때 표시 메시지 분기 //
  function getEmptyMessage() {
    // 팔로워 보기 — 단순 빈 상태 //
    if (!isFollow) return t('myStoreSetting.program.noFollower');

    // 팔로우 검색 — 업체 리스트 자체가 안 들어옴 (로딩중/실패) //
    if (stores.length === 0) return t('myStoreSetting.followModal.listLoading');

    // 검색어와 매칭되는 결과 없음 //
    return t('myStoreSetting.followModal.noSearchResult');
  }

  // 행 렌더링 //
  function renderStatusIcon(variant) {
    if (variant === 'add') {
      return <DottedRing borderColor={theme.line} />;
    }

    if (variant === 'pending') {
      return (
        <PendingIcon backgroundColor={theme.primary}>
          <ClockSvg viewBox='0 0 24 24'>
            <circle cx='12' cy='12' r='9' fill='none' stroke='currentColor' strokeWidth='2' />
            <line x1='12' y1='12' x2='12' y2='7' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
            <line x1='12' y1='12' x2='15.5' y2='14' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
          </ClockSvg>
        </PendingIcon>
      );
    }

    return (
      <CheckCircle backgroundColor={theme.primary}>
        <CheckSvg color={theme.subBackground} />
      </CheckCircle>
    );
  }

  // 체육관 리스트 //
  function renderRow(data, index) {
    const state = isFollow ? getFollowRowState(data) : getFollowerRowState(data);

    return (
      <Row key={'CenterFollowRow-' + index}>
        <RowLeft onClick={() => onClickRowDetail(data)}>
          <RowLogo>
            <UserProfile profile={data.storeLogo} defaultProfile={DefaultLogoPath} />
          </RowLogo>
          <RowTextBlock>
            <RowName color={theme.text}>{data.storeName}</RowName>
            <RowAddress color={theme.subText}>{data.storeAddress}</RowAddress>
          </RowTextBlock>
        </RowLeft>
        <RowRight onClick={() => onClickRow(data)}>
          {state.label && <StatusLabel color={theme.subText}>{state.label}</StatusLabel>}
          {renderStatusIcon(state.variant)}
        </RowRight>
      </Row>
    );
  }

  return (
    <TotalModalWrapper>
      <Modal backgroundColor={theme.subBackground}>
        <HeaderRow>
          <HeaderTextBlock>
            <ModalTitle color={theme.text}>{getTitle()}</ModalTitle>
            <ModalDescription color={theme.subText}>{getDescription()}</ModalDescription>
          </HeaderTextBlock>
          <ExitButton onClick={onClickExitButton}>
            <ExitIcon color={theme.line} />
          </ExitButton>
        </HeaderRow>

        {
          clickData === undefined &&
          <ListViewWrapper>
            <SearchInputBox borderColor={theme.line}>
              <SearchSvg color={theme.line} />
              <SearchTextInput
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={t('myStoreSetting.followModal.searchPlaceholder')}
                placeholderColor={theme.line}
                textColor={theme.text} />
            </SearchInputBox>
            <Divider lineColor={theme.line} />
            <BoxWrapper>
              <ListBox
                scrollColor={theme.scrollBar}
                trackColor={theme.subBackground}
                isEmpty={filteredData.length === 0}>
                {
                  filteredData.length === 0 &&
                  <EmptyText color={theme.subText}>{getEmptyMessage()}</EmptyText>
                }
                {
                  filteredData.length > 0 &&
                  filteredData.map((data, index) => renderRow(data, index))
                }
              </ListBox>
            </BoxWrapper>
          </ListViewWrapper>
        }
        {
          clickData !== undefined &&
          <DetailScrollWrapper>
            <DetailStoreInfo>
              <DetailTextBlock>
                <DetailStoreName color={theme.text}>{clickData.storeName}</DetailStoreName>
                <IconTextRow>
                  <SmallIcon src={PhoneImage} />
                  <DetailMetaText color={theme.subText}>{calcGymTel(clickData.storeTel)}</DetailMetaText>
                </IconTextRow>
                <IconTextRow>
                  <SmallIcon src={LocationImage} />
                  <DetailMetaText color={theme.subText}>{clickData.storeAddress}</DetailMetaText>
                </IconTextRow>
              </DetailTextBlock>
              <DetailLogo>
                <UserProfile profile={clickData.storeLogo} defaultProfile={DefaultLogoPath} />
              </DetailLogo>
            </DetailStoreInfo>
            {/*<ActionButton backgroundColor={calcActionButtonColor()} onClick={onClickFollowAction}>
              <ActionButtonText color={theme.subBackground}>{calcActionButtonText()}</ActionButtonText>
            </ActionButton>*/}
            <PhotosLabel color={theme.subText}>{t('myStoreSetting.followModal.centerPhotos')}</PhotosLabel>
            {
              pictures.length === 0 &&
              <NoneImageBox backgroundColor={theme.buttonFill}>
                <NoneImage src={NonePictureImage} />
                <NoneText color={theme.line}>{t('myStoreSetting.followModal.noImages')}</NoneText>
              </NoneImageBox>
            }
            {
              pictures.length > 0 &&
              <PhotoCarouselWrapper>
                <CenterPictureBox ref={CenterPictureRef}>
                  {
                    pictures.map((url, idx) => (
                      <Picture key={'CenterPicture-' + idx} src={url} />
                    ))
                  }
                </CenterPictureBox>
                <PhotoNavButton
                  position='left'
                  isView={nowPictureIndex > 0}
                  onClick={onClickPrevPicture}>
                  <NavImage src={PrevImage} />
                </PhotoNavButton>
                <PhotoNavButton
                  position='right'
                  isView={nowPictureIndex < pictures.length - 1}
                  onClick={onClickNextPicture}>
                  <NavImage src={NextImage} />
                </PhotoNavButton>
                <DotMarkerRow>
                  {
                    pictures.map((_, idx) => (
                      <DotMark key={'DotMark-' + idx} backgroundColor={nowPictureIndex === idx ? theme.subKey : theme.buttonFill} />
                    ))
                  }
                </DotMarkerRow>
              </PhotoCarouselWrapper>
            }
          </DetailScrollWrapper>
        }
      </Modal>
    </TotalModalWrapper>
  );
}

const Modal = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  padding: 20px;

  gap: 10px;

  height: 700px;
  width: 617px;

  border-radius: 10px;

  background-color: ${props => props.backgroundColor};

  overflow: hidden;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: flex-start;

  padding-left: 10px;

  width: calc(100% - 10px);
`;

const HeaderTextBlock = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  gap: 4px;
`;

const ModalTitle = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 16px;

  margin: 0;

  color: ${props => props.color};
`;

const ModalDescription = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  margin: 0;

  color: ${props => props.color};
`;

const ExitButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 30px;
  height: 30px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const ExitIcon = styled(ExitButtonSvg)`
  width: 20px;
  height: auto;

  & path,
  & rect {
    fill: ${props => props.color};
  }
`;

const SearchInputBox = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  margin: 10px 10px 0 10px;
  padding: 0 10px;

  height: 40px;

  gap: 5px;

  border: 1px solid ${props => props.borderColor};
  border-radius: 5px;
`;

const SearchSvg = styled(SearchIcon)`
  width: 20px;
  height: 20px;

  & path {
    fill: ${props => props.color};
  }
`;

const SearchTextInput = styled.input`
  font-family: NotoSansKR-Medium;
  font-size: 12px;

  flex: 1;

  outline: none;
  border: none;

  background-color: transparent;

  color: ${props => props.textColor};

  ::placeholder {
    color: ${props => props.placeholderColor};
  }
`;

const Divider = styled.div`
  margin: 10px;

  width: calc(100% - 20px);
  height: 1px;

  background-color: ${props => props.lineColor};
`;

const ListViewWrapper = styled.div`
  display: flex;
  flex-direction: column;

  flex: 1;
  min-height: 0;

  gap: 10px;

  width: 100%;
`;

const BoxWrapper = styled.div`
  display: flex;
  flex-direction: column;

  flex: 1;
  min-height: 0;

  width: 100%;
`;

const ListBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: ${props => props.isEmpty ? 'center' : 'flex-start'};
  align-items: ${props => props.isEmpty ? 'center' : 'stretch'};

  flex: 1;

  padding-right: 10px;

  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.scrollColor};
    border-radius: 50px;
  }

  &::-webkit-scrollbar-track {
    background-color: ${props => props.trackColor};
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  padding: 10px;

  height: 75px;
`;

const RowLeft = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  flex: 1;
  min-width: 0;

  gap: 21px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const RowLogo = styled.div`
  flex-shrink: 0;

  width: 55px;
  height: 55px;

  border-radius: 5px;

  overflow: hidden;
`;

const RowTextBlock = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  flex: 1;
  min-width: 0;
`;

const RowName = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 16px;
  line-height: 30px;

  margin: 0;

  color: ${props => props.color};

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

const RowAddress = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;
  line-height: 30px;

  margin: 0;

  color: ${props => props.color};

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

const RowRight = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  flex-shrink: 0;

  padding-left: 10px;

  gap: 7px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const StatusLabel = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  margin: 0;

  color: ${props => props.color};
`;

const DottedRing = styled.div`
  box-sizing: border-box;

  width: 23px;
  height: 23px;

  border: 1.5px dashed ${props => props.borderColor};
  border-radius: 50%;
`;

const PendingIcon = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 23px;
  height: 23px;

  border-radius: 50%;

  background-color: ${props => props.backgroundColor};

  color: ${props => props.theme.subBackground};
`;

const ClockSvg = styled.svg`
  width: 14px;
  height: 14px;

  color: inherit;
`;

const CheckCircle = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 23px;
  height: 23px;

  border-radius: 50%;

  background-color: ${props => props.backgroundColor};
`;

const CheckSvg = styled(CheckIcon)`
  width: 11px;
  height: 9px;

  color: ${props => props.color};
`;

const DetailScrollWrapper = styled.div`
  display: flex;
  flex-direction: column;

  flex: 1;
  min-height: 0;

  padding: 10px;

  gap: 12px;

  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 2px;
  }
`;

const DetailStoreInfo = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: flex-start;

  gap: 12px;

  width: 100%;
`;

const DetailTextBlock = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  flex: 1;
  min-width: 0;

  gap: 6px;
`;

const DetailStoreName = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 16px;

  margin: 0;

  color: ${props => props.color};
`;

const IconTextRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  gap: 4px;
`;

const SmallIcon = styled.img`
  margin-top: 2px;

  width: 16px;
  height: 16px;
`;

const DetailMetaText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  margin: 0;

  color: ${props => props.color};
`;

const DetailLogo = styled.div`
  flex-shrink: 0;

  width: 124px;
  height: 124px;

  border-radius: 5px;

  overflow: hidden;
`;

const ActionButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  flex-shrink: 0;

  width: 100%;
  height: 48px;

  background-color: ${props => props.backgroundColor};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const ActionButtonText = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 14px;

  margin: 0;

  color: ${props => props.color};
`;

const PhotosLabel = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  margin: 4px 0 0 0;

  color: ${props => props.color};
`;

const NoneImageBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  flex-shrink: 0;

  width: 100%;
  height: 280px;

  gap: 18px;

  border-radius: 6px;

  background-color: ${props => props.backgroundColor};
`;

const NoneImage = styled.img`
  width: 60px;
  height: 60px;

  object-fit: contain;
`;

const NoneText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  margin: 0;

  color: ${props => props.color};
`;

const PhotoCarouselWrapper = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;

  flex-shrink: 0;

  width: 100%;
`;

const CenterPictureBox = styled.div`
  display: flex;
  flex-direction: row;

  width: 100%;
  height: 342px;

  border-radius: 6px;

  overflow: hidden;
`;

const Picture = styled.img`
  flex-shrink: 0;

  width: 100%;
  height: 342px;

  object-fit: cover;
`;

const PhotoNavButton = styled.div`
  position: absolute;
  top: 50%;
  ${props => props.position === 'left' ? 'left: 12px;' : 'right: 12px;'}
  transform: translateY(-50%);

  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 28px;
  height: 28px;

  border-radius: 28px;

  background-color: rgba(0, 0, 0, 0.6);

  opacity: ${props => props.isView ? 1 : 0};

  transition: opacity 0.2s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: ${props => props.isView ? 'pointer' : 'default'};
    }
  }
`;

const NavImage = styled.img`
  width: 12px;
  height: 9px;

  object-fit: contain;
`;

const DotMarkerRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  margin-top: 12px;

  gap: 4px;

  width: 100%;
`;

const DotMark = styled.div`
  width: 8px;
  height: 8px;

  border-radius: 8px;

  background-color: ${props => props.backgroundColor};

  transition: all 0.3s ease-in-out;
`;

const EmptyText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  margin: 0;

  text-align: center;
  white-space: pre-line;

  color: ${props => props.color};
`;
